import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  IconButton,
  Button,
  Card,
  Input,
  Typography,
  UserAvatar,
  LoadingIcon,
} from '@/primer/components';
import {
  useAuth,
  useMemberInfo,
  useUpdateMemberInfo,
  useMemberProfilePicture,
  useUploadProfilePicture,
} from '@/api';
import { PageLayout, PageLoadingState } from '@/components/layout';

/**
 * Settings — Profile sub-screen.
 *
 * Allows the user to update their first name, last name, and profile picture.
 * Logic is extracted from the original Settings page.
 */
export function SettingsProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: memberInfo, isLoading: isLoadingMemberInfo } = useMemberInfo();
  const updateMemberInfoMutation = useUpdateMemberInfo();

  const { data: publicProfile, isLoading: isLoadingProfilePicture } = useMemberProfilePicture(
    memberInfo?.id
  );
  const uploadProfilePictureMutation = useUploadProfilePicture();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (memberInfo) {
      setFirstName(memberInfo.firstName || '');
      setLastName(memberInfo.lastName || '');
    }
  }, [memberInfo]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSuccess(false);

    // Required field validation
    const firstNameTrimmed = firstName.trim();
    const lastNameTrimmed = lastName.trim();
    setFirstNameError(firstNameTrimmed ? '' : 'First name is required');
    setLastNameError(lastNameTrimmed ? '' : 'Last name is required');
    if (!firstNameTrimmed || !lastNameTrimmed) return;

    try {
      const updateRequest: { firstName?: string; lastName?: string } = {};
      if (firstName !== memberInfo?.firstName) updateRequest.firstName = firstName;
      if (lastName !== memberInfo?.lastName) updateRequest.lastName = lastName;

      if (Object.keys(updateRequest).length > 0) {
        await updateMemberInfoMutation.mutateAsync(updateRequest);
      }
      setSuccess(true);
    } catch (err) {
      console.error('Failed to update profile:', err);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      try {
        await uploadProfilePictureMutation.mutateAsync(file);
      } catch (err) {
        console.error('Failed to upload profile picture:', err);
        setAvatarPreview(null);
      }
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const isLoading = isLoadingMemberInfo || isLoadingProfilePicture;

  const currentAvatarUrl = avatarPreview || publicProfile?.profileImageUrl;

  const initials =
    firstName || lastName
      ? `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()
      : user?.email?.[0]?.toUpperCase();

  if (isLoading) {
    return <PageLoadingState size="large" />;
  }

  return (
    <PageLayout className="py-6">
      <div className="flex flex-col gap-6">
        {/* Back navigation — mobile only */}
        <div className="md:hidden flex items-center gap-4">
          <IconButton
            icon="chevron-left"
            onClick={() => navigate({ to: '/settings' })}
            aria-label="Back to Settings"
          />
          <Typography variant="header" size="h3" as="h3">
            Settings
          </Typography>
        </div>

        <Typography variant="header" size="h1" as="h1">
          Profile
        </Typography>

        <Card>
          <div className="p-5 flex flex-col gap-5">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <div
                className="relative cursor-pointer group"
                onClick={handleAvatarClick}
                role="button"
                tabIndex={0}
                aria-label="Change profile photo"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleAvatarClick();
                }}
              >
                <UserAvatar
                  userId={memberInfo?.id ?? 0}
                  src={currentAvatarUrl ?? undefined}
                  initials={initials}
                  size="large"
                />
                {uploadProfilePictureMutation.isPending && (
                  <div className="absolute inset-0 bg-black/50 rounded-rounded flex items-center justify-center">
                    <LoadingIcon size="small" className="text-white" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 rounded-rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Typography variant="body" size="xs" color="white">
                    Edit
                  </Typography>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                  aria-label="Upload profile photo"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="header" size="h3">
                  {firstName && lastName ? `${firstName} ${lastName}` : 'Add your name'}
                </Typography>
                <Typography variant="body" size="small" color="bodylighter">
                  Tap to change photo
                </Typography>
              </div>
            </div>

            {/* Success banner */}
            {success && (
              <div className="bg-success border border-success rounded-small px-4 py-3">
                <Typography variant="body" size="small" color="success">
                  Profile updated successfully!
                </Typography>
              </div>
            )}

            {/* Error banner */}
            {updateMemberInfoMutation.error && (
              <div className="bg-error border border-error rounded-small px-4 py-3">
                <Typography variant="body" size="small" color="error">
                  Failed to update profile. Please try again.
                </Typography>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Input
                label="First Name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (e.target.value.trim()) setFirstNameError('');
                }}
                placeholder="First name"
                autoComplete="given-name"
                error={firstNameError || undefined}
              />
              <Input
                label="Last Name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (e.target.value.trim()) setLastNameError('');
                }}
                placeholder="Last name"
                autoComplete="family-name"
                error={lastNameError || undefined}
              />
              <Button type="submit" fullWidth isLoading={updateMemberInfoMutation.isPending}>
                Save changes
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </PageLayout>
  );
}

export default SettingsProfile;
